import { get, set, del } from 'idb-keyval';
import type { PeakData } from './peakGenerator';

const PROXY_PREFIX = 'editor-proxy:';
const WAVEFORM_PREFIX = 'editor-waveform:';
const AUDIO_BUFFER_PREFIX = 'editor-audiobuf:';

export function proxyKey(assetId: string) {
  return `${PROXY_PREFIX}${assetId}`;
}

export function waveformKey(assetId: string) {
  return `${WAVEFORM_PREFIX}${assetId}`;
}

function audioBufferKey(assetId: string) {
  return `${AUDIO_BUFFER_PREFIX}${assetId}`;
}

export async function saveProxy(assetId: string, data: ArrayBuffer): Promise<string> {
  const key = proxyKey(assetId);
  await set(key, data);
  return key;
}

export async function getProxy(assetId: string): Promise<ArrayBuffer | undefined> {
  return get<ArrayBuffer>(proxyKey(assetId));
}

/** Save peak data (new min/max format from peakGenerator) */
export async function saveWaveform(assetId: string, peaks: Float32Array | PeakData): Promise<string> {
  const key = waveformKey(assetId);
  await set(key, peaks);
  return key;
}

/** Get waveform peaks — returns PeakData (new) or Float32Array (legacy) */
export async function getWaveform(assetId: string): Promise<PeakData | Float32Array | undefined> {
  return get<PeakData | Float32Array>(waveformKey(assetId));
}

/** Save the raw proxy ArrayBuffer for AudioBuffer decoding on zoom changes */
export async function saveAudioBuffer(assetId: string, data: ArrayBuffer): Promise<void> {
  await set(audioBufferKey(assetId), data);
}

/** Get raw proxy data for AudioBuffer decoding */
export async function getAudioBufferData(assetId: string): Promise<ArrayBuffer | undefined> {
  return get<ArrayBuffer>(audioBufferKey(assetId));
}

export async function deleteAssetData(assetId: string) {
  await del(proxyKey(assetId));
  await del(waveformKey(assetId));
  await del(audioBufferKey(assetId));
}

/** Check if proxy data exists for an asset (without loading it) */
export async function hasAssetData(assetId: string): Promise<boolean> {
  const data = await get(proxyKey(assetId));
  return data !== undefined;
}

/** Bulk delete all asset data for given IDs */
export async function deleteProjectAssets(assetIds: string[]): Promise<void> {
  await Promise.all(assetIds.map((id) => deleteAssetData(id)));
}
